import { create } from "zustand";
import { progressionApi, type HqCelebrationEvent, type PowerChange, type ProgressionState } from "./progression-api.js";
import { useSession } from "../state/session.js";
import { useBase, errorText } from "./state.js";
import { toast } from "../ui/Toast.js";
import { t } from "../i18n/index.js";

interface ProgressionStore {
  owner: string | null;
  data: ProgressionState | null;
  celebrations: HqCelebrationEvent[];
  error: string | null;
  busy: boolean;
  reset(owner: string | null): void;
  hydrate(owner: string, data: ProgressionState): void;
  refresh(): Promise<void>;
  powerChanged(event: PowerChange): void;
  hqCompleted(event: HqCelebrationEvent): void;
  acknowledge(id: string): Promise<boolean>;
  redeem(cosmeticId: string): Promise<boolean>;
}

let request = 0;
let powerRevision = 0;
let latestPower: ProgressionState["power"] | null = null;
const delivered = new Set<string>();
const acknowledged = new Set<string>();

/** Login snapshots do not generate gains; only unique committed server events do. */
export const useProgression = create<ProgressionStore>((set, get) => ({
  owner: null, data: null, celebrations: [], error: null, busy: false,
  reset(owner) {
    if (owner === get().owner) return;
    request++; powerRevision++; latestPower = null; delivered.clear(); acknowledged.clear();
    set({ owner, data: null, celebrations: [], error: null, busy: false });
  },
  hydrate(owner, data) {
    get().reset(owner); request++; powerRevision++; latestPower = data.power;
    set({ data, celebrations: data.celebrations.filter((event) => !acknowledged.has(event.id)), error: null });
  },
  async refresh() {
    const owner = useSession.getState().account?.id ?? null;
    if (!owner) return;
    get().reset(owner);
    const ticket = ++request;
    const revision = powerRevision;
    const pendingAtStart = new Set(get().celebrations.map((event) => event.id));
    try {
      const data = await progressionApi.state();
      if (owner !== useSession.getState().account?.id || ticket !== request) return;
      const current = get();
      const arrivedDuringRequest = current.celebrations.filter((e) => !pendingAtStart.has(e.id));
      const celebrations = [...new Map([...data.celebrations, ...arrivedDuringRequest].filter((e) => !acknowledged.has(e.id)).map((e) => [e.id, e])).values()].sort((a, b) => a.level - b.level);
      set({ data: revision === powerRevision ? data : { ...data, power: latestPower ?? data.power }, celebrations, error: null });
    } catch (error) {
      if (owner === useSession.getState().account?.id && ticket === request) set({ error: errorText(error) });
    }
  },
  powerChanged(event) {
    const owner = useSession.getState().account?.id;
    if (!owner || owner !== get().owner || !event.id || delivered.has(event.id) || !Number.isFinite(event.delta) || event.after - event.before !== event.delta) return;
    delivered.add(event.id);
    if (delivered.size > 256) delivered.delete(delivered.values().next().value!);
    powerRevision++;
    latestPower = event.power;
    set((s) => ({ data: s.data ? { ...s.data, power: event.power } : null }));
    useBase.setState((s) => ({ base: s.base?.enclave.accountId === owner ? { ...s.base, enclave: { ...s.base.enclave, power: event.after, powerBreakdown: event.power } } : s.base }));
    if (event.delta > 0) toast.reward(t("progression.gain", { n: event.delta.toLocaleString() }));
  },
  hqCompleted(event) {
    if (get().owner !== useSession.getState().account?.id || !event.id || !Number.isInteger(event.level) || acknowledged.has(event.id)) return;
    set((s) => ({ celebrations: [...s.celebrations.filter((e) => e.id !== event.id), event].sort((a, b) => a.level - b.level) }));
    void get().refresh();
  },
  async acknowledge(id) {
    if (get().busy) return false;
    const owner = get().owner;
    set({ busy: true, error: null });
    try {
      await progressionApi.seen(id);
      if (owner !== useSession.getState().account?.id) return false;
      acknowledged.add(id);
      set((s) => ({ celebrations: s.celebrations.filter((e) => e.id !== id) }));
      return true;
    } catch (error) {
      if (owner === useSession.getState().account?.id) set({ error: errorText(error) });
      return false;
    } finally { if (owner === get().owner) set({ busy: false }); }
  },
  async redeem(cosmeticId) {
    if (get().busy) return false;
    const owner = get().owner;
    set({ busy: true, error: null });
    try {
      const data = await progressionApi.skin(cosmeticId);
      if (owner !== useSession.getState().account?.id) return false;
      set({ data });
      toast.reward(t("progression.skinGranted"));
      return true;
    } catch (error) {
      if (owner === useSession.getState().account?.id) set({ error: errorText(error) });
      return false;
    } finally { if (owner === get().owner) set({ busy: false }); }
  },
}));
